import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  inject,
  input,
  OnDestroy,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  getFormulaBuiltinSignatureLabel,
  INDEXCORE_FORMULA_BUILTINS,
  INDEXCORE_FORMULA_LANGUAGE_ID,
  INSERT_AS_SNIPPET,
} from '../../formula/indexcore-formula-builtins';
import { formulaParameterRangesInSignature, getFormulaCallContext } from '../../formula/formula-call-context';

/** 言語・プロバイダを一度だけ登録（複数エディタで重複登録しない） */
let monacoLanguageInitialized = false;

type ITextModel = import('monaco-editor').editor.ITextModel;

/** モデルごとに補完で参照するフィールドキー取得子 */
const modelFieldKeysGetter = new WeakMap<ITextModel, () => readonly string[]>();

let editorInstanceSeq = 0;

/**
 * バンドルされた ESM と整合する Monaco バージョン（package.json の monaco-editor と揃えること）
 * Worker は相対 import が多いため CDN の同一バージョンを参照（オフライン・厳格 CSP では動かない可能性あり）
 */
const MONACO_EDITOR_VERSION = '0.52.2';
const MONACO_ESM_VS_ROOT = `https://cdn.jsdelivr.net/npm/monaco-editor@${MONACO_EDITOR_VERSION}/esm/vs`;

@Component({
  selector: 'app-formula-monaco-editor',
  standalone: true,
  template: '<div class="formula-monaco-root" #host></div>',
  styleUrl: './formula-monaco-editor.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormulaMonacoEditorComponent),
      multi: true,
    },
  ],
})
export class FormulaMonacoEditorComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);

  /** JSON 解析などで得たフィールドキー候補 */
  readonly fieldKeys = input<readonly string[]>([]);

  private readonly host = viewChild.required<ElementRef<HTMLDivElement>>('host');

  private editor: import('monaco-editor').editor.IStandaloneCodeEditor | null = null;
  private model: import('monaco-editor').editor.ITextModel | null = null;

  /** 非同期初期化を打ち切る（破棄後にエディタを作らない） */
  private initAborted = false;

  /** Monaco 初期化前に writeValue された値 */
  private pendingValue = '';

  private onChange: (v: string) => void = () => {};
  private onTouched: () => void = () => {};
  private disabled = false;

  ngAfterViewInit(): void {
    void this.bootstrapMonaco();
  }

  ngOnDestroy(): void {
    this.initAborted = true;
    this.disposeEditor();
  }

  writeValue(value: string | null): void {
    const v = value ?? '';
    this.pendingValue = v;
    this.editor?.setValue(v);
  }

  registerOnChange(fn: (v: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.editor?.updateOptions({ readOnly: isDisabled });
  }

  /** Monaco 環境とエディタを非同期で構築（メインバンドルから monaco を遅延分離） */
  private async bootstrapMonaco(): Promise<void> {
    const el = this.host().nativeElement;
    this.ensureMonacoEnvironment();

    const monaco = await import('monaco-editor');
    if (this.initAborted) return;

    this.ensureLanguageProviders(monaco);

    const uri = monaco.Uri.parse(`inmemory://formula/${++editorInstanceSeq}.icf`);
    const model = monaco.editor.createModel(this.pendingValue, INDEXCORE_FORMULA_LANGUAGE_ID, uri);
    this.model = model;

    // 補完で常に最新の fieldKeys を参照
    modelFieldKeysGetter.set(model, () => this.fieldKeys());

    this.editor = monaco.editor.create(el, {
      model,
      theme: 'vs-dark',
      minimap: { enabled: false },
      lineNumbers: 'off',
      glyphMargin: false,
      folding: false,
      lineDecorationsWidth: 0,
      overviewRulerLanes: 0,
      hideCursorInOverviewRuler: true,
      scrollBeyondLastLine: false,
      wordWrap: 'off',
      fontSize: 15,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      padding: { top: 6, bottom: 6 },
      automaticLayout: true,
      tabSize: 2,
      readOnly: this.disabled,
      quickSuggestions: { other: true, comments: false, strings: true },
      suggestOnTriggerCharacters: true,
      scrollbar: {
        vertical: 'hidden',
        horizontal: 'auto',
        alwaysConsumeMouseWheel: false,
      },
    });

    // Ctrl+;（Mac は Cmd+;）でも補完ウィジェットを開く
    this.editor.addAction({
      id: 'indexcore-formula.triggerSuggestCtrlSemicolon',
      label: 'IndexCore Formula: 補完を表示',
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.Semicolon],
      run: (ed) => {
        void ed.getAction('editor.action.triggerSuggest')?.run();
      },
    });

    this.editor.onDidChangeModelContent(() => {
      this.onChange(this.editor?.getValue() ?? '');
      this.cdr.markForCheck();
    });
    this.editor.onDidBlurEditorWidget(() => this.onTouched());
  }

  private disposeEditor(): void {
    if (this.model) {
      modelFieldKeysGetter.delete(this.model);
      this.model.dispose();
      this.model = null;
    }
    if (this.editor) {
      this.editor.dispose();
      this.editor = null;
    }
  }

  /** Worker を editor 用に解決（各ビルドで ?worker がバンドルされる） */
  private ensureMonacoEnvironment(): void {
    const g = globalThis as unknown as {
      MonacoEnvironment?: {
        getWorker?: (_id: string, _label: string) => Worker;
        getWorkerUrl?: (_id: string, _label: string) => string;
      };
    };
    if (g.MonacoEnvironment?.getWorkerUrl || g.MonacoEnvironment?.getWorker) return;
    g.MonacoEnvironment = {
      getWorkerUrl(_workerId: string, label: string): string {
        switch (label) {
          case 'json':
            return `${MONACO_ESM_VS_ROOT}/language/json/json.worker.js`;
          case 'css':
          case 'scss':
          case 'less':
            return `${MONACO_ESM_VS_ROOT}/language/css/css.worker.js`;
          case 'html':
          case 'handlebars':
          case 'razor':
            return `${MONACO_ESM_VS_ROOT}/language/html/html.worker.js`;
          case 'typescript':
          case 'javascript':
            return `${MONACO_ESM_VS_ROOT}/language/typescript/ts.worker.js`;
          default:
            return `${MONACO_ESM_VS_ROOT}/editor/editor.worker.js`;
        }
      },
    };
  }

  private ensureLanguageProviders(monaco: typeof import('monaco-editor')): void {
    if (monacoLanguageInitialized) return;
    monacoLanguageInitialized = true;

    monaco.languages.register({ id: INDEXCORE_FORMULA_LANGUAGE_ID });

    const kw = INDEXCORE_FORMULA_BUILTINS.map((b) => b.name).join('|');
    monaco.languages.setMonarchTokensProvider(INDEXCORE_FORMULA_LANGUAGE_ID, {
      tokenizer: {
        root: [
          [new RegExp(`\\b(?:${kw})\\b`, 'i'), 'keyword'],
          [/'(?:[^'\\\\]|\\\\.)*'/, 'string'],
          [/"(?:[^"\\\\]|\\\\.)*"/, 'string'],
          [/\{[^}]+\}/, 'string'],
          [/\d+(?:\.\d+)?/, 'number'],
          // ハイフンはクラス末尾に置き、無効な範囲指定を避ける
          [/[[\](){}+,*/:=.\-]/, 'delimiter'],
          [/[a-zA-Z_][\\w]*/, 'identifier'],
        ],
      },
    });

    const snippetRule = monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet;

    monaco.languages.registerCompletionItemProvider(INDEXCORE_FORMULA_LANGUAGE_ID, {
      triggerCharacters: ['(', ',', "'", '{'],
      provideCompletionItems: (model, position) => {
        const keysFn = modelFieldKeysGetter.get(model);
        let keys = keysFn ? [...keysFn()] : [];

        const wordUntil = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: wordUntil.startColumn,
          endColumn: wordUntil.endColumn,
        };
        const prefix = wordUntil.word.toLowerCase();

        // キー数が多いときはプレフィックスなしでは候補を絞り、補完 UI の重さを抑える
        const maxKeysNoPrefix = 100;
        if (!prefix && keys.length > maxKeysNoPrefix) {
          keys = keys.slice(0, maxKeysNoPrefix).sort((a, b) => a.localeCompare(b));
        }

        const suggestions: import('monaco-editor').languages.CompletionItem[] = [];

        for (const b of INDEXCORE_FORMULA_BUILTINS) {
          if (prefix && !b.name.toLowerCase().startsWith(prefix)) continue;
          // label.description: 用途の一言（長すぎると一覧で省略されやすい）
          suggestions.push({
            label: { label: b.name, description: b.usage },
            kind: monaco.languages.CompletionItemKind.Function,
            filterText: b.name,
            detail: b.detail,
            insertText: b.insertText,
            insertTextRules: b.insertTextRules === INSERT_AS_SNIPPET ? snippetRule : undefined,
            documentation: { value: b.documentation },
            range,
            sortText: `0_${b.name}`,
          });
        }

        const keyMatches = (k: string): boolean => {
          if (!prefix) return true;
          const low = k.toLowerCase();
          return low.startsWith(prefix) || low.includes(prefix);
        };

        for (const k of keys) {
          if (!keyMatches(k)) continue;
          suggestions.push({
            label: { label: k, description: '元データの列名' },
            kind: monaco.languages.CompletionItemKind.Field,
            filterText: k,
            detail: 'alias / calc で参照',
            insertText: k,
            documentation: { value: '`alias` や `calc` の式で、元データの項目名として使う。' },
            range,
            sortText: `1_${k}`,
          });
          suggestions.push({
            label: { label: `{${k}}`, description: '式の中で値参照' },
            kind: monaco.languages.CompletionItemKind.Field,
            filterText: `{${k}}`,
            detail: 'round 等の第1引数',
            insertText: `{${k}}`,
            documentation: { value: '`round` / `floor` / `ceiling` のように、式の中カッコで項目値を参照する。' },
            range,
            sortText: `2_${k}`,
          });
        }

        return { suggestions };
      },
    });

    monaco.languages.registerSignatureHelpProvider(INDEXCORE_FORMULA_LANGUAGE_ID, {
      signatureHelpTriggerCharacters: ['(', ','],
      signatureHelpRetriggerCharacters: [','],
      provideSignatureHelp: (model, position) => {
        const offset = model.getOffsetAt(position);
        const full = model.getValue();
        const before = full.slice(0, offset);
        const ctx = getFormulaCallContext(before);
        if (!ctx) return null;
        const builtin = INDEXCORE_FORMULA_BUILTINS.find(
          (b) => b.name.toLowerCase() === ctx.name.toLowerCase(),
        );
        if (!builtin) return null;

        const sigLabel = getFormulaBuiltinSignatureLabel(builtin);
        const paramLabels = builtin.signatureParameters.map((p) => p.label);
        const ranges = formulaParameterRangesInSignature(sigLabel, paramLabels);

        const parameters: import('monaco-editor').languages.ParameterInformation[] =
          builtin.signatureParameters.map((p, i) => {
            const r = ranges[i];
            const label: [number, number] | string =
              r && r[1] > r[0] ? [r[0], r[1]] : p.label;
            return {
              label,
              documentation: { value: p.documentation },
            };
          });

        const lastIdx = Math.max(0, parameters.length - 1);
        const activeParameter =
          parameters.length === 0 ? 0 : Math.min(ctx.activeParameter, lastIdx);

        const signatureHelp: import('monaco-editor').languages.SignatureHelp = {
          signatures: [
            {
              label: sigLabel,
              documentation: { value: builtin.documentation },
              parameters,
            },
          ],
          activeSignature: 0,
          activeParameter,
        };

        return { value: signatureHelp, dispose: () => {} };
      },
    });

    monaco.languages.registerHoverProvider(INDEXCORE_FORMULA_LANGUAGE_ID, {
      provideHover: (model, position) => {
        const word = model.getWordAtPosition(position);
        if (!word) return null;
        const hit = INDEXCORE_FORMULA_BUILTINS.find(
          (b) => b.name.toLowerCase() === word.word.toLowerCase(),
        );
        if (!hit) return null;
        return {
          range: new monaco.Range(
            position.lineNumber,
            word.startColumn,
            position.lineNumber,
            word.endColumn,
          ),
          contents: [{ value: hit.documentation }],
        };
      },
    });
  }
}
